// src/utils/emailUtils.js
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  collection, 
  query, 
  orderBy, 
  where, 
  limit, 
  startAfter, 
  getDocs, 
  doc, 
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp 
} from 'firebase/firestore';
import { storage, db } from '../firebase/config';

const getMimeTypeFromName = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  const mimeTypes = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    txt: 'text/plain',
    csv: 'text/csv',
    html: 'text/html',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
    json: 'application/json',
    xml: 'application/xml'
  };
  return mimeTypes[ext] || 'application/octet-stream';
};

export const emailService = {
  /**
   * Send an email using the cloud function (maintains audit trail)
   */
  sendEmail: async (emailData) => {
      if (emailData.attachments && emailData.attachments.length > 0) {
        return emailService.sendEmailWithAttachments(emailData);
      } else {
        const functions = getFunctions();
        const sendEmailFn = httpsCallable(functions, 'sendEmail');
        const result = await sendEmailFn(emailData);
        return result.data;
      }
    },

  convertMarkdownToHTML: (markdown) => {
    if (!markdown) return '';
    
    let html = markdown;
    
    // Convert headers
    html = html.replace(/^### (.*$)/gim, '<h3 style="color: #333; margin: 20px 0 10px 0;">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 style="color: #333; margin: 25px 0 15px 0;">$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1 style="color: #333; margin: 30px 0 20px 0;">$1</h1>');
    
    // Convert bold and italic
    html = html.replace(/\*\*\*(.*?)\*\*\*/gim, '<strong><em>$1</em></strong>');
    html = html.replace(/\*\*(.*?)\*\*/gim, '<strong style="font-weight: bold;">$1</strong>');
    html = html.replace(/\*(.*?)\*/gim, '<em style="font-style: italic;">$1</em>');
    
    // Convert links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" style="color: #2563eb; text-decoration: none;">$1</a>');
    
    // Convert blockquotes
    html = html.replace(/^> (.*$)/gim, '<blockquote style="border-left: 4px solid #e5e7eb; margin: 20px 0; padding: 10px 20px; background-color: #f9fafb; font-style: italic;">$1</blockquote>');
    
    // Convert horizontal rules
    html = html.replace(/^---$/gim, '<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 30px 0;">');
    
    // Convert unordered lists
    html = html.replace(/^\- (.*$)/gim, '<li style="margin: 5px 0;">$1</li>');
    html = html.replace(/(<li.*?>.*?<\/li>)/gims, '<ul style="margin: 15px 0; padding-left: 30px;">$1</ul>');
    
    // Convert ordered lists
    html = html.replace(/^\d+\. (.*$)/gim, '<li style="margin: 5px 0;">$1</li>');
    
    // Convert line breaks and paragraphs
    html = html.replace(/\n\n/gim, '</p><p style="margin: 15px 0;">');
    html = html.replace(/\n/gim, '<br>');
    
    // Wrap in paragraphs if not already wrapped
    if (!html.includes('<p>') && !html.includes('<h1>') && !html.includes('<h2>') && !html.includes('<h3>')) {
      html = '<p style="margin: 15px 0;">' + html + '</p>';
    }
    
    return html;
  },

  uploadEmailAttachments: async (organizationId, emailId, attachments) => {
    try {
      const uploadedAttachments = [];
      
      for (const attachment of attachments) {
        if (attachment.content && attachment.name) {
          // Convert base64 to blob
          const byteCharacters = atob(attachment.content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: attachment.type });
          
          // Upload to storage
          const timestamp = Date.now();
          const fileName = `${timestamp}_${attachment.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
          const storageRef = ref(
            storage, 
            `organizations/${organizationId}/emails/${emailId}/attachments/${fileName}`
          );
          
          await uploadBytes(storageRef, blob);
          const downloadURL = await getDownloadURL(storageRef);
          
          uploadedAttachments.push({
            id: attachment.id,
            name: attachment.name,
            size: attachment.size,
            type: attachment.type,
            url: downloadURL,
            uploadedAt: new Date().toISOString()
          });
        }
      }
      
      return uploadedAttachments;
    } catch (error) {
      console.error('Error uploading email attachments:', error);
      throw new Error(`Failed to upload attachments: ${error.message}`);
    }
  },

  /**
   * Download attachment content from storage
   */
  downloadAttachment: async (attachmentUrl) => {
    try {
      const response = await fetch(attachmentUrl);
      if (!response.ok) {
        throw new Error('Failed to download attachment');
      }
      
      const blob = await response.blob();
      
      // Convert blob to base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1]; // Remove data:... prefix
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      throw error;
    }
  },

  /**
   * Prepare attachments for forwarding/editing by downloading from storage
   */
  prepareAttachmentsForEdit: async (attachments) => {
    try {
      const preparedAttachments = [];
      
      for (const attachment of attachments) {
        if (attachment.url && attachment.name) {
          try {
            const content = await emailService.downloadAttachment(attachment.url);
            const mimeType = attachment.type || getMimeTypeFromName(attachment.name) || 'application/octet-stream';
            
            preparedAttachments.push({
              id: attachment.id,
              name: attachment.name,
              size: attachment.size,
              type: mimeType, // Ensure MIME type is set
              content: content
            });
          } catch (error) {
            console.warn(`Failed to download attachment ${attachment.name}:`, error);
            // Skip this attachment but continue with others
          }
        }
      }
      
      return preparedAttachments;
    } catch (error) {
      console.error('Error preparing attachments:', error);
      return [];
    }
  },

  /**
   * Enhanced send email with attachment storage
   */
  sendEmailWithAttachments: async (emailData) => {
    const functions = getFunctions();
    const sendEmailFn = httpsCallable(functions, 'sendEmail');
    
    try {
      // Validate attachments before processing
      const validAttachments = emailData.attachments.filter(att => {
        const isValid = att.name && att.content && att.mimeType;
        if (!isValid) {
          console.warn('Invalid attachment filtered out:', {
            name: att.name,
            hasContent: !!att.content,
            mimeType: att.mimeType
          });
        }
        return isValid;
      });

      if (validAttachments.length !== emailData.attachments.length) {
        console.warn(`Filtered out ${emailData.attachments.length - validAttachments.length} invalid attachments`);
      }

      // First upload attachments if any
      let uploadedAttachments = [];
      const emailId = `email_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      if (validAttachments.length > 0) {
        // Convert mimeType back to type for upload function
        const attachmentsForUpload = validAttachments.map(att => ({
          ...att,
          type: att.mimeType
        }));
        
        uploadedAttachments = await emailService.uploadEmailAttachments(
          emailData.organizationId, 
          emailId, 
          attachmentsForUpload
        );
      }
      
      // Send email with validated attachments
      const result = await sendEmailFn({
        ...emailData,
        emailId: emailId,
        attachments: validAttachments
      });
      
      // Return result with uploaded attachment info for history storage
      return {
        ...result.data,
        emailId: emailId,
        storedAttachments: uploadedAttachments
      };
    } catch (error) {
      console.error('Error sending email with attachments:', error);
      throw error;
    }
  },

  /**
   * Get email history with improved error handling and validation
   */
  // src/utils/emailUtils.js - Simplified getEmailHistory
getEmailHistory: async (organizationId, options = {}) => {
  try {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    console.log('Fetching emails for organization:', organizationId);

    const {
      limitCount = 50,
      lastDoc = null,
      filters = {},
      orderByField = 'sentAt',
      orderDirection = 'desc'
    } = options;

    // Validate limit
    if (limitCount > 100) {
      throw new Error('Maximum limit is 100 emails per request');
    }

    // Build query
    const emailsRef = collection(db, 'parrot-mail', organizationId, 'emails');
    let queryConstraints = [orderBy(orderByField, orderDirection)];
    
    // Add simple filters
    if (filters.status && filters.status !== 'all') {
      queryConstraints.push(where('status', '==', filters.status));
    }
    
    if (lastDoc) {
      queryConstraints.push(startAfter(lastDoc));
    }
    
    queryConstraints.push(limit(limitCount + 1));
    
    const q = query(emailsRef, ...queryConstraints);
    const snapshot = await getDocs(q);
    
    console.log('Query executed, docs found:', snapshot.size);
    
    const emails = [];
    let lastDocument = null;

    // Fix: Use regular for loop instead of forEach
    const docs = snapshot.docs;
    for (let i = 0; i < docs.length; i++) {
      console.log(`Processing doc ${i}:`, docs[i].id);
      
      if (i < limitCount) {
        const data = docs[i].data();
        const email = {
          id: docs[i].id,
          ...data,
          // Convert Firestore Timestamp to ISO string safely
          sentAt: data.sentAt?.toDate ? data.sentAt.toDate().toISOString() : data.sentAt,
          // Ensure arrays are properly formatted
          to: Array.isArray(data.to) ? data.to : (data.to ? [data.to] : []),
          cc: Array.isArray(data.cc) ? data.cc : (data.cc ? [data.cc] : []),
          bcc: Array.isArray(data.bcc) ? data.bcc : (data.bcc ? [data.bcc] : []),
          attachments: Array.isArray(data.attachments) ? data.attachments : []
        };
        
        emails.push(email);
        lastDocument = docs[i];
        console.log('Added email:', email.subject);
      }
    }

    const hasMore = docs.length > limitCount;
    
    console.log('Final result - emails:', emails.length, 'hasMore:', hasMore);

    return {
      emails,
      hasMore,
      lastDoc: lastDocument,
      total: emails.length
    };
  } catch (error) {
    console.error('Error getting email history:', error);
    
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied. You may not have access to this organization\'s emails.');
    } else if (error.code === 'unauthenticated') {
      throw new Error('Authentication required to access email history.');
    }
    
    throw new Error(`Failed to fetch email history: ${error.message}`);
  }
},

  /**
   * Get a single email by ID with validation
   */
  getEmailById: async (organizationId, emailId) => {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      
      if (!emailId) {
        throw new Error('Email ID is required');
      }

      const emailRef = doc(db, 'parrot-mail', organizationId, 'emails', emailId);
      const emailSnap = await getDoc(emailRef);
      
      if (emailSnap.exists()) {
        const data = emailSnap.data();
        return {
          id: emailSnap.id,
          ...data,
          sentAt: data.sentAt?.toDate()?.toISOString() || null,
          to: Array.isArray(data.to) ? data.to : (data.to ? [data.to] : []),
          cc: Array.isArray(data.cc) ? data.cc : (data.cc ? [data.cc] : []),
          bcc: Array.isArray(data.bcc) ? data.bcc : (data.bcc ? [data.bcc] : [])
        };
      } else {
        throw new Error('Email not found');
      }
    } catch (error) {
      console.error('Error getting email:', error);
      
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied. You may not have access to this email.');
      }
      
      throw error;
    }
  },

  /**
   * Get email statistics with better aggregation
   */
  getEmailStats: async (organizationId, timeRange = '30d') => {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const daysBack = parseInt(timeRange.replace('d', ''));
      if (isNaN(daysBack) || daysBack <= 0) {
        throw new Error('Invalid time range format. Use format like "30d"');
      }

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);

      const emailsRef = collection(db, 'parrot-mail', organizationId, 'emails');
      const q = query(
        emailsRef,
        where('sentAt', '>=', Timestamp.fromDate(startDate)),
        orderBy('sentAt', 'desc'),
        limit(1000) // Reasonable limit for stats calculation
      );

      const snapshot = await getDocs(q);
      
      const stats = {
        total: 0,
        sent: 0,
        failed: 0,
        delivered: 0,
        opened: 0,
        byModule: {},
        byDay: {},
        byStatus: {}
      };

      snapshot.forEach(doc => {
        const data = doc.data();
        stats.total++;

        // Count by status
        const status = data.status || 'unknown';
        stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
        
        // Legacy counters for backwards compatibility
        if (status === 'sent') stats.sent++;
        else if (status === 'failed') stats.failed++;
        else if (status === 'delivered') stats.delivered++;
        else if (status === 'opened') stats.opened++;

        // Count by module
        if (data.module) {
          stats.byModule[data.module] = (stats.byModule[data.module] || 0) + 1;
        }

        // Count by day
        if (data.sentAt) {
          const day = data.sentAt.toDate().toISOString().split('T')[0];
          stats.byDay[day] = (stats.byDay[day] || 0) + 1;
        }
      });

      return {
        ...stats,
        timeRange,
        startDate: startDate.toISOString(),
        endDate: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error getting email stats:', error);
      
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied. You may not have access to this organization\'s email statistics.');
      }
      
      throw new Error(`Failed to fetch email statistics: ${error.message}`);
    }
  },

  uploadTemplateHeaderImage: async (organizationId, templateType, imageFile) => {
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${imageFile.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(
        storage, 
        `organizations/${organizationId}/email-templates/${templateType}/headers/${fileName}`
      );
      
      await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading header image:', error);
      throw error;
    }
  },

  uploadTemplateAttachment: async (organizationId, templateType, attachmentFile) => {
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${attachmentFile.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(
        storage, 
        `organizations/${organizationId}/email-templates/${templateType}/attachments/${fileName}`
      );
      
      await uploadBytes(storageRef, attachmentFile);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw error;
    }
  },

  generateEmailTemplate: async (prompt, templateType, organizationInfo, options = {}) => {
    const functions = getFunctions();
    const generateTemplateFn = httpsCallable(functions, 'generateEmailTemplate');
    
    try {
      if (!prompt || !prompt.trim()) {
        throw new Error('Prompt is required for template generation');
      }
      
      if (!templateType) {
        throw new Error('Template type is required');
      }
      
      if (!organizationInfo || !organizationInfo.name) {
        throw new Error('Organization information with name is required');
      }
      
      const {
        includeHeader = true,
        includeFooter = true,
        colorScheme = 'professional',
        tone = 'professional'
      } = options;
      
      const result = await generateTemplateFn({
        prompt,
        templateType,
        organizationInfo,
        includeHeader,
        includeFooter,
        colorScheme,
        tone
      });
      return result.data;
    } catch (error) {
      console.error('Error generating email template:', error);
      if (error.code) {
        throw new Error(`Template generation error (${error.code}): ${error.message}`);
      }
      throw error;
    }
  },

  /**
   * Template methods using cloud functions (these remain the same)
   */
  getEmailTemplate: async (organizationId, templateType) => {
    try {
      // Check organization templates first
      const orgTemplateRef = doc(db, 'parrot-mail', organizationId, 'email-templates', templateType);
      const orgTemplateSnap = await getDoc(orgTemplateRef);
      
      if (orgTemplateSnap.exists()) {
        return {
          template: orgTemplateSnap.data(),
          source: 'organization'
        };
      }
      
      // Fall back to system templates
      const systemTemplateRef = doc(db, 'parrot-mail-templates', templateType);
      const systemTemplateSnap = await getDoc(systemTemplateRef);
      
      if (systemTemplateSnap.exists()) {
        return {
          template: systemTemplateSnap.data(),
          source: 'system'
        };
      }
      
      // Return default template
      const defaultTemplate = getDefaultTemplateForModule(templateType, organizationId);
      return {
        template: defaultTemplate,
        source: 'default'
      };
    } catch (error) {
      console.error('Error getting email template:', error);
      throw error;
    }
  },
  
  saveEmailTemplate: async (organizationId, templateType, template) => {
    try {
      const templateRef = doc(db, 'parrot-mail', organizationId, 'email-templates', templateType);
      
      const templateData = {
        name: template.name,
        subject: template.subject || '',
        htmlContent: template.htmlContent || '',
        headerImageUrl: template.headerImageUrl || null,
        attachments: template.attachments || [],
        module: template.module || templateType,
        type: template.type || templateType,
        updatedAt: Timestamp.now()
      };
      
      // Check if exists to add createdAt
      const existing = await getDoc(templateRef);
      if (!existing.exists()) {
        templateData.createdAt = Timestamp.now();
      }
      
      await setDoc(templateRef, templateData, { merge: true });
      
      return {
        success: true,
        template: templateData
      };
    } catch (error) {
      console.error('Error saving email template:', error);
      throw error;
    }
  },

  getAllEmailTemplates: async (organizationId) => {
    try {
      const templates = [];
      const moduleTypes = [
        'general', 'customer', 'order', 'billing', 
        'newsletter', 'support', 'marketing', 'notification'
      ];
      
      // Get organization templates
      const orgTemplatesRef = collection(db, 'parrot-mail', organizationId, 'email-templates');
      const orgTemplatesSnap = await getDocs(orgTemplatesRef);
      
      const orgTemplates = new Map();
      orgTemplatesSnap.forEach(doc => {
        orgTemplates.set(doc.id, {
          id: doc.id,
          ...doc.data(),
          source: 'organization'
        });
      });
      
      // For each module type, get template (org first, then system, then default)
      for (const moduleType of moduleTypes) {
        if (orgTemplates.has(moduleType)) {
          templates.push(orgTemplates.get(moduleType));
        } else {
          // Try system template
          const systemTemplateRef = doc(db, 'parrot-mail-templates', moduleType);
          const systemTemplateSnap = await getDoc(systemTemplateRef);
          
          if (systemTemplateSnap.exists()) {
            templates.push({
              id: moduleType,
              ...systemTemplateSnap.data(),
              source: 'system'
            });
          } else {
            // Use default
            const defaultTemplate = getDefaultTemplateForModule(moduleType, organizationId);
            templates.push({
              id: moduleType,
              ...defaultTemplate,
              source: 'default'
            });
          }
        }
      }
      
      return templates;
    } catch (error) {
      console.error('Error getting all email templates:', error);
      throw error;
    }
  },

  // ... [rest of the methods remain the same but with similar validation patterns]
  
  /**
   * Validate organization access (utility method)
   */
  validateOrganizationAccess: async (organizationId) => {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      // Try to read organization document to validate access
      const orgRef = doc(db, 'parrot-organizations', organizationId);
      const orgSnap = await getDoc(orgRef);
      
      if (!orgSnap.exists()) {
        throw new Error('Organization not found');
      }
      
      return orgSnap.data();
    } catch (error) {
      if (error.code === 'permission-denied') {
        throw new Error('You do not have access to this organization');
      }
      throw error;
    }
  },

  /**
   * Search emails with text search (client-side filtering)
   */
  searchEmails: async (organizationId, searchTerm, options = {}) => {
    try {
      // Get recent emails first
      const emailHistory = await emailService.getEmailHistory(organizationId, {
        limitCount: options.limit || 100,
        ...options
      });
      
      if (!searchTerm || !searchTerm.trim()) {
        return emailHistory;
      }
      
      const searchLower = searchTerm.toLowerCase();
      const filteredEmails = emailHistory.emails.filter(email => {
        return (
          email.subject?.toLowerCase().includes(searchLower) ||
          email.to?.some(recipient => 
            recipient.toLowerCase().includes(searchLower)
          ) ||
          email.cc?.some(recipient => 
            recipient.toLowerCase().includes(searchLower)
          ) ||
          email.htmlBody?.toLowerCase().includes(searchLower) ||
          email.textBody?.toLowerCase().includes(searchLower)
        );
      });
      
      return {
        ...emailHistory,
        emails: filteredEmails,
        searchTerm,
        originalCount: emailHistory.emails.length,
        filteredCount: filteredEmails.length
      };
    } catch (error) {
      console.error('Error searching emails:', error);
      throw new Error(`Failed to search emails: ${error.message}`);
    }
  }, 

  generateDefaultTemplate: (organizationInfo, title, content, options = {}) => {
    const {
      name = 'Your Organization',
      logo = '',
      primaryColor = '#2563eb',
      email = '',
      website = '',
    } = organizationInfo;
    
    const {
      footerText = 'This email was sent via your business management system',
      buttons = [],
      headerImageUrl = null
    } = options;
    
    // Generate buttons HTML if any
    const buttonsHtml = buttons.length > 0 ? `
      <div style="margin: 20px 0; text-align: center;">
        ${buttons.map(button => `
          <a href="${button.url}" style="display: inline-block; margin: 0 10px; padding: 10px 20px; text-decoration: none; border-radius: 4px; font-weight: bold; 
            ${button.primary 
              ? `background-color: ${primaryColor}; color: white; border: 1px solid ${primaryColor};` 
              : `background-color: white; color: ${primaryColor}; border: 1px solid ${primaryColor};`}">
            ${button.text}
          </a>
        `).join('')}
      </div>
    ` : '';
    
    // Header can be either a logo, image banner, or text
    const headerHtml = headerImageUrl 
      ? `<div style="width: 100%;"><img src="${headerImageUrl}" alt="${name}" style="width: 100%; max-height: 200px; object-fit: cover;" /></div>`
      : logo 
        ? `<div style="background-color: ${primaryColor}; padding: 20px; text-align: center;">
             <img src="${logo}" alt="${name}" style="max-height: 50px; max-width: 200px;" />
           </div>`
        : `<div style="background-color: ${primaryColor}; padding: 20px; text-align: center;">
             <h2 style="color: white; margin: 0;">${name}</h2>
           </div>`;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>${title}</title>
      </head>
      <body style="font-family: Arial, sans-serif; margin: 0; padding: 0; background-color: #f5f5f5;">
        <div style="max-width: 600px; margin: 0 auto; background-color: white; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
          <!-- Header -->
          ${headerHtml}
          
          <!-- Content -->
          <div style="padding: 30px 20px;">
            <h2 style="margin-top: 0; color: #333;">${title}</h2>
            
            ${content}
            
            ${buttonsHtml}
          </div>
          
          <!-- Footer -->
          <div style="padding: 20px; background-color: #f5f5f5; text-align: center; font-size: 12px; color: #666;">
            <p>${footerText}</p>
            ${email || website ? `
              <p>
                ${email ? `Email: <a href="mailto:${email}" style="color: ${primaryColor};">${email}</a>` : ''}
                ${email && website ? ' | ' : ''}
                ${website ? `Website: <a href="${website}" style="color: ${primaryColor};">${website}</a>` : ''}
              </p>
            ` : ''}
            <p>© ${new Date().getFullYear()} ${name}. All rights reserved.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }
};

function getDefaultTemplateForModule(moduleType, organizationId) {
  return {
    name: `Default ${moduleType.charAt(0).toUpperCase() + moduleType.slice(1)} Template`,
    subject: `{{organization.name}} - ${moduleType} communication`,
    htmlContent: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="padding: 20px;">
          <p>Dear {{customer.name}},</p>
          <div>{{content}}</div>
          <p>Best regards,<br/>{{organization.name}} Team</p>
        </div>
      </div>
    `,
    module: moduleType,
    type: moduleType,
    headerImageUrl: null,
    attachments: []
  };
}

export const {
  sendEmail,
  getEmailTemplate,
  saveEmailTemplate,
  getEmailHistory,
  getEmailStats,
  generateEmailTemplate,
  generateAIContent,
  uploadTemplateHeaderImage,
  uploadTemplateAttachment,
  saveEmailDraft,
  getEmailDrafts,
  deleteEmailDraft,
  validateEmail,
  extractTextFromHTML,
  formatFileSize,
  generateDefaultTemplate
} = emailService;

export default emailService;